import {
    closestCenter,
    type CollisionDetection,
    type DragEndEvent,
    type DragMoveEvent,
    type DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
    type DeepPartial,
    type MoneyMapRuleGroup as MoneyMapRuleGroupType,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
    type RuleOrGroup,
} from '@fruitful/types';
import { sortBy } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    DELETE_LIGHT_BG,
    DELETE_RED,
    DELETE_SHADOW,
    DELETE_THRESHOLD,
    DRAG_PINK,
    DRAG_PINK_SHADOW,
    goalDragRingShadow,
    GROUP_GOAL_ROW_MIN_HEIGHT,
} from './goalsWaterfallConstants';
import type { ActiveDrag, DropIndicator } from './goalsWaterfallTypes';

interface UseGoalsWaterfallDnDParams {
    goalRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    goalItems: RuleOrGroup[];
    groups: Array<DeepPartial<MoneyMapRuleGroupType>>;
    topLevelIds: string[];
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
    onDeleteGroup: (groupId: string) => Promise<void>;
    onDeleteGoal?: (ruleId: string) => Promise<void>;
    refetchMoneyMap?: () => Promise<unknown>;
    getGroupRules: (
        groupId: string | undefined,
    ) => Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    /** Returns the freshest groups from the React Query cache (post-refetch). */
    getLatestMoneyMapGroups?: () => Array<DeepPartial<MoneyMapRuleGroupType>>;
}

interface UseGoalsWaterfallDnDReturn {
    activeDrag: ActiveDrag;
    dropIndicator: DropIndicator;
    getDropIndicator: () => DropIndicator;
    sensors: ReturnType<typeof useSensors>;
    collisionDetectionStrategy: CollisionDetection;
    handleDragStart: (event: DragStartEvent) => void;
    handlePresetDragStart: (event: DragStartEvent) => void;
    handleDragMove: (event: DragMoveEvent) => void;
    handleDragEnd: (event: DragEndEvent) => Promise<void>;
    handleDragCancel: () => void;
    activeDragGoalItem: RuleOrGroup | null;
    activeDragGroupGoal: DeepPartial<MoneyMapRuleWithAccountDetails> | null;
    overlayWrapperStyle: React.CSSProperties;
    isPendingDelete: boolean;
}

export type GoalsWaterfallDnDControls = UseGoalsWaterfallDnDReturn;

/** Captures the event-local variables that each drop handler needs. */
interface DropHandlerContext {
    activeId: string;
    overId: string;
    allRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    currentDrag: NonNullable<ActiveDrag>;
    currentDropIndicator: DropIndicator;
    activeGroup: DeepPartial<MoneyMapRuleGroupType> | undefined;
    overGroup: DeepPartial<MoneyMapRuleGroupType> | undefined;
    overGroupGoalRule: DeepPartial<MoneyMapRuleWithAccountDetails> | undefined;
}

// ---------------------------------------------------------------------------
// Module-level pure utilities
// ---------------------------------------------------------------------------

/**
 * Reads the live DOM size for the sortable node. `active.rect.current.initial`
 * can be wrong for native div measurers; this matches the visible tile.
 */
const readLiveDraggableSize = (
    id: string,
): { width: number; height: number } | null => {
    if (typeof document === 'undefined') {
        return null;
    }
    const safeId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(id)
            : id;
    const el = document.querySelector<HTMLElement>(
        `[data-goals-waterfall-draggable-id="${safeId}"]`,
    );
    if (el == null) {
        return null;
    }
    const { width, height } = el.getBoundingClientRect();
    if (width < 2 || height < 2) {
        return null;
    }
    return { width, height };
};

/** Resolves a drop indicator based on vertical proximity to threshold zones. */
const computeThresholdIndicator = (
    activeCenterY: number,
    overCenterY: number,
    threshold: number,
    centerKind: 'outline' | 'group-outline',
    targetId: string,
): DropIndicator => {
    if (activeCenterY < overCenterY - threshold)
        return { kind: 'line-before', targetId };
    if (activeCenterY > overCenterY + threshold)
        return { kind: 'line-after', targetId };
    return { kind: centerKind, targetId };
};

/**
 * Applies an updated top-level priority to a single standalone goal rule.
 * Returns the rule unchanged if it is not a top-level goal (bills, spend, or grouped).
 */
const reapplyGoalPriority = <
    T extends DeepPartial<MoneyMapRuleWithAccountDetails>,
>(
    rule: T,
    topPriorityMap: Map<string | undefined, number>,
): T => {
    if (rule.type !== MoneyMapRuleType.GOAL || rule.moneyMapRuleGroupId != null)
        return rule;
    const newP = topPriorityMap.get(rule.id);
    return newP != null ? { ...rule, priority: newP } : rule;
};

/**
 * Strips nested rules from groups and applies updated top-level priorities.
 * An optional `filter` predicate can exclude specific groups (e.g. an empty source group).
 */
const stripAndUpdateGroupPriorities = (
    groups: Array<DeepPartial<MoneyMapRuleGroupType>>,
    topPriorityMap: Map<string | undefined, number>,
    filter?: (g: DeepPartial<MoneyMapRuleGroupType>) => boolean,
) =>
    groups
        .filter(filter ?? (() => true))
        .map(({ moneyMapRules: _nested, ...g }) => {
            const newP = topPriorityMap.get(g.id);
            return newP != null ? { ...g, priority: newP } : g;
        });

// ---------------------------------------------------------------------------
// useSyncedState — keeps a ref and React state in sync with a single setter.
// ---------------------------------------------------------------------------

function useSyncedState<T>(
    initial: T,
): [T, React.MutableRefObject<T>, (val: T) => void] {
    const [state, setState] = useState<T>(initial);
    const ref = useRef<T>(initial);
    const set = useCallback((val: T) => {
        ref.current = val;
        setState(val);
    }, []);
    return [state, ref, set];
}

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

export const useGoalsWaterfallDnD = ({
    goalRules,
    goalItems,
    groups,
    topLevelIds,
    moneyMap,
    onUpdateMoneyMap,
    onDeleteGroup,
    onDeleteGoal,
    refetchMoneyMap,
    getGroupRules,
    getLatestMoneyMapGroups,
}: UseGoalsWaterfallDnDParams): UseGoalsWaterfallDnDReturn => {
    const [activeDrag, setActiveDrag] = useState<ActiveDrag>(null);
    const [dropIndicator, dropIndicatorRef, setDropIndicatorBoth] =
        useSyncedState<DropIndicator>(null);
    const [isPendingDelete, isPendingDeleteRef, setIsPendingDeleteBoth] =
        useSyncedState(false);
    const lastValidOverIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (activeDrag == null) return;
        document.body.style.cursor = 'grabbing';
        return () => {
            document.body.style.cursor = '';
        };
    }, [activeDrag]);

    // Bills + spend rules occupy positions 1…N; goal items start at N+1.
    const nonGoalRuleCount = useMemo(
        () =>
            (moneyMap.moneyMapRules ?? []).filter(
                (r) =>
                    r.type === MoneyMapRuleType.BILLS ||
                    r.type === MoneyMapRuleType.SPEND,
            ).length,
        [moneyMap.moneyMapRules],
    );

    const buildTopLevelPriorityMap = useCallback(
        (items: RuleOrGroup[]): Map<string | undefined, number> =>
            new Map(
                items.map((item, i) => [item.id, nonGoalRuleCount + i + 1]),
            ),
        [nonGoalRuleCount],
    );

    const collisionDetectionStrategy = useCallback<CollisionDetection>(
        (args) => {
            const activeId = args.active.id.toString();
            const presetRuleCount = args.active.data.current
                ?.presetRuleCount as number | undefined;
            const activeIsSingleGoalPreset =
                presetRuleCount != null && presetRuleCount === 1;
            const activeIsMultiGoalPreset =
                presetRuleCount != null && presetRuleCount > 1;

            const activeGroupGoal = goalRules.find(
                (r) => r.id === activeId && r.moneyMapRuleGroupId != null,
            );

            if (activeGroupGoal != null) {
                // Expose only same-group siblings + top-level items. Excluding
                // other-group rules ensures closestCenter resolves to the group
                // card itself when hovering over another group, giving reliable
                // drop indicator feedback via the threshold logic in handleDragMove.
                const sameGroupIds = new Set(
                    goalRules
                        .filter(
                            (r) =>
                                r.moneyMapRuleGroupId ===
                                activeGroupGoal.moneyMapRuleGroupId,
                        )
                        .map((r) => r.id),
                );
                const topLevelItemIds = new Set(goalItems.map((i) => i.id));

                // When the active item is dragged outside the source group's
                // bounding rect, restrict collision to top-level droppables only.
                // This lets `over` resolve to the group card itself rather than
                // a same-group rule, enabling the "escape to top level" path.
                const sourceGroupContainer = args.droppableContainers.find(
                    (c) =>
                        c.id.toString() === activeGroupGoal.moneyMapRuleGroupId,
                );
                const groupRect = sourceGroupContainer?.rect.current;
                const translated = args.active.rect.current.translated;
                if (groupRect != null && translated != null) {
                    const activeCenterY =
                        translated.top + translated.height / 2;
                    const isOutsideGroup =
                        activeCenterY < groupRect.top ||
                        activeCenterY > groupRect.bottom;
                    if (isOutsideGroup) {
                        return closestCenter({
                            ...args,
                            droppableContainers:
                                args.droppableContainers.filter((c) =>
                                    topLevelItemIds.has(c.id.toString()),
                                ),
                        });
                    }
                }

                const eligibleIds = new Set([
                    ...sameGroupIds,
                    ...topLevelItemIds,
                ]);
                return closestCenter({
                    ...args,
                    droppableContainers: args.droppableContainers.filter((c) =>
                        eligibleIds.has(c.id.toString()),
                    ),
                });
            }

            const topLevelItemIds = new Set(goalItems.map((i) => i.id));
            const activeIsTopGroup =
                goalItems.some(
                    (i) => i.id === activeId && i.entityType === 'group',
                ) || activeIsMultiGoalPreset;
            const activeGroupRuleCount = activeIsSingleGoalPreset
                ? 1
                : goalRules.filter((r) => r.moneyMapRuleGroupId === activeId)
                      .length;

            if (
                (activeIsTopGroup && activeGroupRuleCount === 1) ||
                activeIsSingleGoalPreset
            ) {
                // When the single-goal group's center is physically inside a
                // target group's bounding rect, collide with that group's inner
                // rules so the user can insert at a specific position (line
                // indicator). When outside every group, restrict to top-level
                // items so reorder lines can appear at group edges.
                const translated = args.active.rect.current.translated;
                if (translated != null) {
                    const activeCenterY =
                        translated.top + translated.height / 2;
                    for (const item of goalItems) {
                        if (item.entityType !== 'group' || item.id == null)
                            continue;
                        const groupContainer = args.droppableContainers.find(
                            (c) => c.id.toString() === item.id,
                        );
                        const groupRect = groupContainer?.rect.current;
                        if (
                            groupRect == null ||
                            activeCenterY < groupRect.top ||
                            activeCenterY > groupRect.bottom
                        )
                            continue;
                        const groupRuleContainers =
                            args.droppableContainers.filter((c) => {
                                const rule = goalRules.find(
                                    (r) => r.id === c.id.toString(),
                                );
                                return rule?.moneyMapRuleGroupId === item.id;
                            });
                        if (groupRuleContainers.length > 0) {
                            return closestCenter({
                                ...args,
                                droppableContainers: groupRuleContainers,
                            });
                        }
                    }
                }
                return closestCenter({
                    ...args,
                    droppableContainers: args.droppableContainers.filter((c) =>
                        topLevelItemIds.has(c.id.toString()),
                    ),
                });
            }

            // For standalone top-goal drags (and single-goal presets): when the
            // active item's center is physically inside a group's bounding rect,
            // collide against only that group's rule droppables (not the group
            // card). closestCenter would otherwise almost always pick the group
            // card's center over individual rule centers, so the positional line
            // never appears. When outside every group, fall back to top-level-only
            // collision.
            if (!activeIsTopGroup && !activeIsSingleGoalPreset) {
                const translated = args.active.rect.current.translated;
                if (translated != null) {
                    const activeCenterY =
                        translated.top + translated.height / 2;
                    for (const item of goalItems) {
                        if (item.entityType !== 'group' || item.id == null)
                            continue;
                        const groupContainer = args.droppableContainers.find(
                            (c) => c.id.toString() === item.id,
                        );
                        const groupRect = groupContainer?.rect.current;
                        if (
                            groupRect == null ||
                            activeCenterY < groupRect.top ||
                            activeCenterY > groupRect.bottom
                        )
                            continue;
                        // Active center is inside this group — collide with its rules.
                        const groupRuleContainers =
                            args.droppableContainers.filter((c) => {
                                const rule = goalRules.find(
                                    (r) => r.id === c.id.toString(),
                                );
                                return rule?.moneyMapRuleGroupId === item.id;
                            });
                        if (groupRuleContainers.length > 0) {
                            return closestCenter({
                                ...args,
                                droppableContainers: groupRuleContainers,
                            });
                        }
                    }
                }
            }

            return closestCenter({
                ...args,
                droppableContainers: args.droppableContainers.filter((c) =>
                    topLevelItemIds.has(c.id.toString()),
                ),
            });
        },
        [goalRules, goalItems],
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            // Distance-based activation: drags start as soon as the pointer moves,
            // without the sluggish fixed delay. Taps without movement stay clicks for
            // opening goal / group editors (listeners are only on the drag handle).
            activationConstraint: { distance: 6 },
        }),
    );

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            const id = event.active.id.toString();
            const initialRect = event.active.rect.current.initial;
            const data = event.active.data.current;
            const live = readLiveDraggableSize(id);
            const height =
                live?.height ??
                initialRect?.height ??
                (data?.type === 'group-goal' ? GROUP_GOAL_ROW_MIN_HEIGHT : 64);
            const width = live?.width ?? initialRect?.width ?? 300;

            let type: NonNullable<ActiveDrag>['type'] = 'top-goal';
            if (data?.type === 'group-goal') {
                type = 'group-goal';
            } else if (
                goalItems.some((i) => i.id === id && i.entityType === 'group')
            ) {
                type = 'top-group';
            }

            setActiveDrag({ id, type, height, width });
            setDropIndicatorBoth(null);
            setIsPendingDeleteBoth(false);
            lastValidOverIdRef.current = null;
        },
        [goalItems, setDropIndicatorBoth, setIsPendingDeleteBoth],
    );

    const handlePresetDragStart = useCallback(
        (event: DragStartEvent) => {
            const id = event.active.id.toString();
            const initialRect = event.active.rect.current.initial;
            const presetRuleCount =
                (event.active.data.current?.presetRuleCount as
                    | number
                    | undefined) ?? 1;
            const height = initialRect?.height ?? 64;
            const width = initialRect?.width ?? 300;

            setActiveDrag({
                id,
                type: 'preset',
                height,
                width,
                presetRuleCount,
            });
            setDropIndicatorBoth(null);
            setIsPendingDeleteBoth(false);
            lastValidOverIdRef.current = null;
        },
        [setDropIndicatorBoth, setIsPendingDeleteBoth],
    );

    const handleDragMove = useCallback(
        (event: DragMoveEvent) => {
            const { active, over, delta } = event;

            // Detect rightward swipe → delete mode. top-goal and group-goal always
            // support delete. top-group only supports delete when it has a single rule
            // (the group and its rule are removed together).
            const isSingleGoalGroup =
                activeDrag?.type === 'top-group' &&
                getGroupRules(activeDrag.id).length === 1;
            const isDeletingNow =
                delta.x > DELETE_THRESHOLD &&
                (activeDrag?.type !== 'top-group' || isSingleGoalGroup);
            if (isDeletingNow !== isPendingDeleteRef.current) {
                setIsPendingDeleteBoth(isDeletingNow);
                if (isDeletingNow) setDropIndicatorBoth(null);
            }
            if (isPendingDeleteRef.current) return;

            const isPresetDrag = activeDrag?.type === 'preset';

            if (over == null || active.id === over.id) {
                if (over == null) {
                    // When past the final list item, show line-after so the user can drop
                    // at the very end. Otherwise preserve the last valid indicator — a
                    // transient over=null can fire just before DragEnd and would otherwise
                    // clear the group-outline indicator the drop logic needs.
                    const lastId = lastValidOverIdRef.current;
                    const lastItemId = topLevelIds[topLevelIds.length - 1];
                    if (
                        !isPresetDrag &&
                        lastId != null &&
                        lastId === lastItemId &&
                        lastId !== active.id.toString()
                    ) {
                        setDropIndicatorBoth({
                            kind: 'line-after',
                            targetId: lastId,
                        });
                    } else if (isPresetDrag) {
                        setDropIndicatorBoth(null);
                    }
                }
                // When active.id === over.id (hovering over own placeholder), preserve
                // the current indicator so the user's merge/combine intent is not lost.
                return;
            }

            lastValidOverIdRef.current = over.id.toString();

            const overId = over.id.toString();
            const activeId = active.id.toString();

            const overGroupGoal = goalRules.find(
                (r) => r.id === overId && r.moneyMapRuleGroupId != null,
            );
            if (overGroupGoal != null) {
                const translated = active.rect.current.translated;
                if (!translated) {
                    setDropIndicatorBoth(null);
                    return;
                }
                const activeCenterY = translated.top + translated.height / 2;
                const overCenterY = over.rect.top + over.rect.height / 2;
                // Always propagate groupId so the parent group receives its pink
                // outline whenever anything is positioned inside it.
                const groupId = overGroupGoal.moneyMapRuleGroupId ?? undefined;
                setDropIndicatorBoth(
                    activeCenterY < overCenterY
                        ? { kind: 'line-before', targetId: overId, groupId }
                        : { kind: 'line-after', targetId: overId, groupId },
                );
                return;
            }

            const overIsGroup = goalItems.some(
                (i) => i.id === overId && i.entityType === 'group',
            );
            const activeIsSingleGoalPreset =
                activeDrag?.type === 'preset' &&
                (activeDrag.presetRuleCount ?? 1) === 1;
            const activeIsMultiGoalPreset =
                activeDrag?.type === 'preset' &&
                (activeDrag.presetRuleCount ?? 1) > 1;
            const activeIsGroup =
                activeDrag?.type === 'top-group' ||
                activeIsMultiGoalPreset ||
                goalItems.some(
                    (i) => i.id === activeId && i.entityType === 'group',
                );
            const activeIsSingleGoalGroup =
                activeIsSingleGoalPreset ||
                (activeIsGroup && getGroupRules(activeId).length === 1);

            const translated = active.rect.current.translated;
            if (!translated) {
                setDropIndicatorBoth(null);
                return;
            }

            const activeCenterY = translated.top + translated.height / 2;
            const overCenterY = over.rect.top + over.rect.height / 2;
            const threshold = over.rect.height * 0.3;

            if (overIsGroup) {
                if (isPresetDrag) {
                    if (activeIsSingleGoalPreset) {
                        const indicator = computeThresholdIndicator(
                            activeCenterY,
                            overCenterY,
                            threshold,
                            'group-outline',
                            overId,
                        );
                        setDropIndicatorBoth(
                            indicator?.kind === 'group-outline'
                                ? indicator
                                : null,
                        );
                    } else {
                        setDropIndicatorBoth(null);
                    }
                    return;
                }

                if (activeIsGroup && !activeIsSingleGoalGroup) {
                    // Multi-goal groups can only reorder — no center merge zone.
                    setDropIndicatorBoth(
                        activeCenterY < overCenterY
                            ? { kind: 'line-before', targetId: overId }
                            : { kind: 'line-after', targetId: overId },
                    );
                } else {
                    // Single-goal groups, group-goals, and standalone top-goals
                    // all use the threshold merge zone.
                    setDropIndicatorBoth(
                        computeThresholdIndicator(
                            activeCenterY,
                            overCenterY,
                            threshold,
                            'group-outline',
                            overId,
                        ),
                    );
                }
            }
        },
        [
            activeDrag,
            getGroupRules,
            goalItems,
            goalRules,
            isPendingDeleteRef,
            setDropIndicatorBoth,
            setIsPendingDeleteBoth,
            topLevelIds,
        ],
    );

    // ---------------------------------------------------------------
    // Handler: group-goal drag (reorder within group, move to another
    // group, or escape to top level).
    // ---------------------------------------------------------------
    const handleGroupGoalDrop = useCallback(
        async (ctx: DropHandlerContext): Promise<boolean> => {
            const {
                currentDrag,
                activeId,
                overId,
                allRules,
                currentDropIndicator,
            } = ctx;

            if (currentDrag.type !== 'group-goal') return false;

            const activeRule = allRules.find((r) => r.id === activeId);
            if (activeRule?.moneyMapRuleGroupId == null) return true;

            const sourceGroupId = activeRule.moneyMapRuleGroupId;
            const remainingSourceRules = sortBy(
                allRules.filter(
                    (r) =>
                        r.moneyMapRuleGroupId === sourceGroupId &&
                        r.id !== activeId,
                ),
                'priority',
            ).map((r, i) => ({ ...r, priority: i + 1 }));
            const sourceGroupBecomesEmpty = remainingSourceRules.length === 0;
            const sourceRuleMap = new Map(
                remainingSourceRules.map((r) => [r.id, r]),
            );

            const groupFilter = (g: DeepPartial<MoneyMapRuleGroupType>) =>
                !sourceGroupBecomesEmpty || g.id !== sourceGroupId;

            // Same-group reorder
            const sameGroupOverRule = allRules.find(
                (r) =>
                    r.id === overId && r.moneyMapRuleGroupId === sourceGroupId,
            );
            if (sameGroupOverRule != null) {
                const groupGoals = sortBy(
                    allRules.filter(
                        (r) => r.moneyMapRuleGroupId === sourceGroupId,
                    ),
                    'priority',
                );
                const oldIndex = groupGoals.findIndex((r) => r.id === activeId);
                const newIndex = groupGoals.findIndex((r) => r.id === overId);
                if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex)
                    return true;

                const withNewPriorities = arrayMove(
                    groupGoals,
                    oldIndex,
                    newIndex,
                ).map((rule, i) => ({ ...rule, priority: i + 1 }));
                const updatedGroupIds = new Set(
                    withNewPriorities.map((r) => r.id),
                );
                let groupIdx = 0;
                const updatedRules = allRules.map((rule) => {
                    if (!updatedGroupIds.has(rule.id)) return rule;
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    return withNewPriorities[groupIdx++]!;
                });
                await onUpdateMoneyMap({ moneyMapRules: updatedRules });
                await refetchMoneyMap?.();
                return true;
            }

            // Move to a different group (center-zone drop)
            const targetGroup = groups.find((g) => g.id === overId);
            if (
                targetGroup != null &&
                targetGroup.id !== sourceGroupId &&
                currentDropIndicator?.kind === 'group-outline'
            ) {
                const newPriority = getGroupRules(overId).length + 1;
                const adjustedTopLevel = sourceGroupBecomesEmpty
                    ? goalItems.filter((i) => i.id !== sourceGroupId)
                    : goalItems;
                const topPriorityMap =
                    buildTopLevelPriorityMap(adjustedTopLevel);

                const updatedRules = allRules.map((rule) => {
                    if (rule.id === activeId)
                        return {
                            ...rule,
                            moneyMapRuleGroupId: targetGroup.id,
                            priority: newPriority,
                        };
                    const sourceUpdated = sourceRuleMap.get(rule.id);
                    if (sourceUpdated != null) return sourceUpdated;
                    return reapplyGoalPriority(rule, topPriorityMap);
                });
                await onUpdateMoneyMap({
                    moneyMapRules: updatedRules,
                    moneyMapRuleGroups: stripAndUpdateGroupPriorities(
                        groups,
                        topPriorityMap,
                        groupFilter,
                    ),
                });
                if (sourceGroupBecomesEmpty) await onDeleteGroup(sourceGroupId);
                await refetchMoneyMap?.();
                return true;
            }

            // Escape to top level — create a new single-goal group for the
            // escaping rule rather than leaving it ungrouped.
            const targetTopLevelItem = goalItems.find((i) => i.id === overId);
            if (
                targetTopLevelItem != null &&
                (currentDropIndicator?.kind === 'line-before' ||
                    currentDropIndicator?.kind === 'line-after')
            ) {
                let newTopLevelItems = sourceGroupBecomesEmpty
                    ? goalItems.filter((i) => i.id !== sourceGroupId)
                    : [...goalItems];

                const targetIndex = newTopLevelItems.findIndex(
                    (i) => i.id === overId,
                );
                const insertIndex =
                    currentDropIndicator.kind === 'line-before'
                        ? targetIndex
                        : targetIndex + 1;

                // Insert a placeholder to compute the new group's priority.
                const newGroupPlaceholderId = `__new_group_${activeId}__`;
                newTopLevelItems = [
                    ...newTopLevelItems.slice(0, insertIndex),
                    {
                        id: newGroupPlaceholderId,
                        entityType: 'group' as const,
                        priority: 0,
                    },
                    ...newTopLevelItems.slice(insertIndex),
                ];

                const topPriorityMap =
                    buildTopLevelPriorityMap(newTopLevelItems);
                const newGroupPriority =
                    topPriorityMap.get(newGroupPlaceholderId) ??
                    nonGoalRuleCount + 1;
                const newGroupStub = {
                    moneyMapId: moneyMap.id ?? '',
                    priority: newGroupPriority,
                    percentage: activeRule.percentage ?? 0,
                    name: activeRule.description ?? 'Goal Set',
                };

                // Call 1: Create the new group stub and update existing group
                // priorities and remaining source-group rule priorities.
                // The escaped rule is not reassigned yet — deleting the source
                // group before the rule is moved would cascade-delete it on
                // the backend.
                await onUpdateMoneyMap({
                    moneyMapRules: allRules.map((rule) => {
                        const sourceUpdated = sourceRuleMap.get(rule.id);
                        if (sourceUpdated != null) return sourceUpdated;
                        return reapplyGoalPriority(rule, topPriorityMap);
                    }),
                    moneyMapRuleGroups: [
                        ...stripAndUpdateGroupPriorities(
                            groups,
                            topPriorityMap,
                            groupFilter,
                        ),
                        newGroupStub,
                    ],
                });

                const knownGroupIds = new Set(groups.map((g) => g.id));
                const latestGroups = getLatestMoneyMapGroups?.() ?? [];
                const newGroup = latestGroups.find(
                    (g) => !knownGroupIds.has(g.id),
                );
                if (newGroup?.id == null) {
                    await refetchMoneyMap?.();
                    return true;
                }

                // Call 2: Assign the escaped rule to the new group.
                await onUpdateMoneyMap({
                    moneyMapRules: allRules.map((rule) => {
                        if (rule.id === activeId)
                            return {
                                ...activeRule,
                                moneyMapRuleGroupId: newGroup.id,
                                priority: 1,
                            };
                        const sourceUpdated = sourceRuleMap.get(rule.id);
                        if (sourceUpdated != null) return sourceUpdated;
                        return reapplyGoalPriority(rule, topPriorityMap);
                    }),
                });
                if (sourceGroupBecomesEmpty) await onDeleteGroup(sourceGroupId);
                await refetchMoneyMap?.();
            }

            return true;
        },
        [
            buildTopLevelPriorityMap,
            getGroupRules,
            getLatestMoneyMapGroups,
            goalItems,
            groups,
            moneyMap,
            nonGoalRuleCount,
            onDeleteGroup,
            onUpdateMoneyMap,
            refetchMoneyMap,
        ],
    );

    // ---------------------------------------------------------------
    // Handler: single-goal group merged into another group (center zone).
    // A single-goal group dropped onto any group is a merge. We skip the
    // indicator check when both targets are single-goal groups — a
    // last-moment over=null in DragMove can wipe the ref, causing a
    // false-negative. Merge only applies to center-zone (group-outline)
    // drops; edge drops fall through to the top-level reorder block.
    // ---------------------------------------------------------------
    const handleSingleGoalGroupMerge = useCallback(
        async (ctx: DropHandlerContext): Promise<boolean> => {
            const {
                currentDrag,
                activeId,
                overId,
                allRules,
                currentDropIndicator,
                activeGroup,
                overGroup,
            } = ctx;

            if (
                currentDrag.type !== 'top-group' ||
                activeGroup == null ||
                overGroup == null ||
                getGroupRules(activeId).length !== 1 ||
                currentDropIndicator?.kind !== 'group-outline'
            )
                return false;

            const activeRule = getGroupRules(activeId)[0];
            if (activeRule == null) return true;

            const remainingTopLevelItems = goalItems.filter(
                (i) => i.id !== activeId,
            );
            const topPriorityMap = buildTopLevelPriorityMap(
                remainingTopLevelItems,
            );

            const updatedRules = allRules.map((rule) => {
                if (rule.id === activeRule.id) {
                    return {
                        ...rule,
                        moneyMapRuleGroupId: overGroup.id,
                        priority: getGroupRules(overId).length + 1,
                    };
                }
                return reapplyGoalPriority(rule, topPriorityMap);
            });

            // Update rule before deleting source group — deleting first cascades-
            // deletes its rules on the backend, breaking the subsequent rule update.
            await onUpdateMoneyMap({
                moneyMapRules: updatedRules,
                moneyMapRuleGroups: stripAndUpdateGroupPriorities(
                    groups,
                    topPriorityMap,
                    (g) => g.id !== activeId,
                ),
            });
            await onDeleteGroup(activeId);
            await refetchMoneyMap?.();
            return true;
        },
        [
            buildTopLevelPriorityMap,
            getGroupRules,
            goalItems,
            groups,
            onDeleteGroup,
            onUpdateMoneyMap,
            refetchMoneyMap,
        ],
    );

    // ---------------------------------------------------------------
    // Handler: single-goal group dropped at a specific position inside
    // another group (line-before / line-after on a group-goal).
    // ---------------------------------------------------------------
    const handleSingleGoalGroupPositionalMerge = useCallback(
        async (ctx: DropHandlerContext): Promise<boolean> => {
            const {
                currentDrag,
                activeId,
                overId,
                allRules,
                currentDropIndicator,
                activeGroup,
                overGroupGoalRule,
            } = ctx;

            if (
                currentDrag.type !== 'top-group' ||
                activeGroup == null ||
                overGroupGoalRule == null ||
                getGroupRules(activeId).length !== 1 ||
                (currentDropIndicator?.kind !== 'line-before' &&
                    currentDropIndicator?.kind !== 'line-after')
            )
                return false;

            const activeRule = getGroupRules(activeId)[0];
            if (
                activeRule == null ||
                overGroupGoalRule.moneyMapRuleGroupId == null
            )
                return true;

            const targetGroupId = overGroupGoalRule.moneyMapRuleGroupId;
            const targetGroupRules = sortBy(
                getGroupRules(targetGroupId),
                'priority',
            );
            const overIndex = targetGroupRules.findIndex(
                (r) => r.id === overId,
            );
            const insertIndex =
                currentDropIndicator?.kind === 'line-before'
                    ? overIndex
                    : overIndex + 1;

            const reorderedGroupRules = [...targetGroupRules];
            reorderedGroupRules.splice(insertIndex, 0, {
                ...activeRule,
                moneyMapRuleGroupId: targetGroupId,
            });
            const withNewPriorities = reorderedGroupRules.map((rule, i) => ({
                ...rule,
                priority: i + 1,
                moneyMapRuleGroupId: targetGroupId,
            }));
            const updatedRuleMap = new Map(
                withNewPriorities.map((r) => [r.id, r]),
            );

            const remainingTopLevelItems = goalItems.filter(
                (i) => i.id !== activeId,
            );
            const topPriorityMap = buildTopLevelPriorityMap(
                remainingTopLevelItems,
            );

            const updatedRules = allRules.map((rule) => {
                const updated = updatedRuleMap.get(rule.id);
                if (updated != null) return updated;
                return reapplyGoalPriority(rule, topPriorityMap);
            });

            await onUpdateMoneyMap({
                moneyMapRules: updatedRules,
                moneyMapRuleGroups: stripAndUpdateGroupPriorities(
                    groups,
                    topPriorityMap,
                    (g) => g.id !== activeId,
                ),
            });
            await onDeleteGroup(activeId);
            await refetchMoneyMap?.();
            return true;
        },
        [
            buildTopLevelPriorityMap,
            getGroupRules,
            goalItems,
            groups,
            onDeleteGroup,
            onUpdateMoneyMap,
            refetchMoneyMap,
        ],
    );

    // ---------------------------------------------------------------
    // Handler: top-level reorder (fallthrough — reorder groups and
    // standalone rules within the top-level list).
    // ---------------------------------------------------------------
    const handleTopLevelReorder = useCallback(
        async (ctx: DropHandlerContext): Promise<void> => {
            const { activeId, overId, allRules } = ctx;

            const oldIndex = goalItems.findIndex((i) => i.id === activeId);
            const newIndex = goalItems.findIndex((i) => i.id === overId);
            if (oldIndex === -1 || newIndex === -1) return;

            // Number from position (bills/spend first, then goals) rather than
            // reusing the previous slots. Reusing slots preserves any bad values
            // already saved (at or below the number of bills/spend rules they
            // render as "-1th"/"0th"), so a reorder could never repair them.
            const reordered = arrayMove(goalItems, oldIndex, newIndex).map(
                (item, i) => ({
                    ...item,
                    priority: nonGoalRuleCount + i + 1,
                }),
            );

            const updatedGoalRulesById = new Map(
                reordered
                    .filter((item) => item.entityType === 'rule')
                    .map((item) => [item.id, item.priority]),
            );
            const updatedGroupsById = new Map(
                reordered
                    .filter((item) => item.entityType === 'group')
                    .map((item) => [item.id, item.priority]),
            );

            const updatedMoneyMapRules = allRules.map((rule) => {
                if (
                    rule.type !== MoneyMapRuleType.GOAL ||
                    rule.moneyMapRuleGroupId != null
                )
                    return rule;
                const newPriority = updatedGoalRulesById.get(rule.id);
                return newPriority != null
                    ? { ...rule, priority: newPriority }
                    : rule;
            });

            // Strip nested rules from groups — sending them would let the backend
            // reprocess in-group priorities. Only the group's top-level priority changes here.
            const updatedGroups = groups.map(
                ({ moneyMapRules: _nested, ...group }) => {
                    const newPriority = updatedGroupsById.get(group.id);
                    return newPriority != null
                        ? { ...group, priority: newPriority }
                        : group;
                },
            );

            await onUpdateMoneyMap({
                moneyMapRules: updatedMoneyMapRules,
                moneyMapRuleGroups: updatedGroups,
            });
            await refetchMoneyMap?.();
        },
        [
            goalItems,
            groups,
            nonGoalRuleCount,
            onUpdateMoneyMap,
            refetchMoneyMap,
        ],
    );

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            const currentDrag = activeDrag;
            const currentDropIndicator = dropIndicatorRef.current;
            const currentIsPendingDelete = isPendingDeleteRef.current;

            setActiveDrag(null);
            setDropIndicatorBoth(null);
            setIsPendingDeleteBoth(false);

            if (currentDrag == null) return;

            // Delete takes priority over any drop action.
            if (currentIsPendingDelete) {
                if (currentDrag.type === 'top-group') {
                    // Single-goal group — delete the group (cascades to the rule on the backend).
                    await onDeleteGroup(currentDrag.id);
                    await refetchMoneyMap?.();
                } else if (onDeleteGoal != null) {
                    await onDeleteGoal(currentDrag.id);
                    await refetchMoneyMap?.();
                }

                // Re-compact goal group priorities so there are no gaps.
                // After a delete, remaining groups may jump from e.g. [3,5,6] → [3,4,5]
                // so getSyntheticGoalPriorityFromActual shows 1st/2nd/3rd not 1st/3rd/4th.
                const latestGroups = getLatestMoneyMapGroups?.() ?? [];
                if (latestGroups.length > 0) {
                    const sorted = [...latestGroups].sort(
                        (a, b) => (a.priority ?? 0) - (b.priority ?? 0),
                    );
                    const updatedGroups = sorted.map((g, i) => {
                        const newPriority = nonGoalRuleCount + i + 1;
                        return {
                            ...g,
                            priority: newPriority,
                            moneyMapRules: (g.moneyMapRules ?? []).map((r) => ({
                                ...r,
                                priority: newPriority,
                            })),
                        };
                    });
                    await onUpdateMoneyMap({
                        moneyMapRuleGroups: updatedGroups,
                    });
                }

                return;
            }

            const effectiveOverId =
                over != null ? over.id.toString() : lastValidOverIdRef.current;

            if (
                effectiveOverId == null ||
                active.id.toString() === effectiveOverId
            ) {
                return;
            }

            const activeId = active.id.toString();
            const overId = effectiveOverId;
            const allRules = moneyMap.moneyMapRules ?? [];
            const ctx: DropHandlerContext = {
                activeId,
                overId,
                allRules,
                currentDrag,
                currentDropIndicator,
                activeGroup: groups.find((g) => g.id === activeId),
                overGroup: groups.find((g) => g.id === overId),
                overGroupGoalRule: allRules.find(
                    (r) => r.id === overId && r.moneyMapRuleGroupId != null,
                ),
            };

            if (await handleGroupGoalDrop(ctx)) return;
            if (await handleSingleGoalGroupMerge(ctx)) return;
            if (await handleSingleGoalGroupPositionalMerge(ctx)) return;
            await handleTopLevelReorder(ctx);
        },
        [
            activeDrag,
            dropIndicatorRef,
            groups,
            handleGroupGoalDrop,
            handleSingleGoalGroupMerge,
            handleSingleGoalGroupPositionalMerge,
            handleTopLevelReorder,
            isPendingDeleteRef,
            moneyMap,
            onDeleteGoal,
            onDeleteGroup,
            refetchMoneyMap,
            setDropIndicatorBoth,
            setIsPendingDeleteBoth,
        ],
    );

    const handleDragCancel = useCallback(() => {
        setActiveDrag(null);
        setDropIndicatorBoth(null);
        setIsPendingDeleteBoth(false);
        lastValidOverIdRef.current = null;
    }, [setDropIndicatorBoth, setIsPendingDeleteBoth]);

    const activeDragGoalItem = useMemo(() => {
        if (activeDrag == null || activeDrag.type === 'group-goal') return null;
        return goalItems.find((i) => i.id === activeDrag.id) ?? null;
    }, [activeDrag, goalItems]);

    const activeDragGroupGoal = useMemo(() => {
        if (activeDrag?.type !== 'group-goal') return null;
        return goalRules.find((r) => r.id === activeDrag.id) ?? null;
    }, [activeDrag, goalRules]);

    const borderRadius = activeDrag?.type === 'group-goal' ? 12 : 16;

    const overlayBaseBox: React.CSSProperties = {
        width: activeDrag?.width,
        height: activeDrag?.height,
        minWidth: activeDrag?.width,
        minHeight: activeDrag?.height,
        boxSizing: 'border-box',
        overflow: 'hidden',
        borderRadius,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        flexShrink: 0,
    };

    const overlayWrapperStyle: React.CSSProperties = isPendingDelete
        ? {
              ...overlayBaseBox,
              opacity: 1,
              cursor: 'grabbing',
              backgroundColor: DELETE_LIGHT_BG,
              boxShadow: `${goalDragRingShadow(DELETE_RED)}, 0 8px 24px ${DELETE_SHADOW}, 0 4px 12px rgba(0,0,0,0.08)`,
          }
        : {
              ...overlayBaseBox,
              opacity: 0.8,
              cursor: 'grabbing',
              boxShadow: `${goalDragRingShadow(DRAG_PINK)}, 0 8px 24px ${DRAG_PINK_SHADOW}, 0 4px 12px rgba(0,0,0,0.08)`,
          };

    const getDropIndicator = useCallback(
        () => dropIndicatorRef.current,
        [dropIndicatorRef],
    );

    return {
        activeDrag,
        dropIndicator,
        getDropIndicator,
        sensors,
        collisionDetectionStrategy,
        handleDragStart,
        handlePresetDragStart,
        handleDragMove,
        handleDragEnd,
        handleDragCancel,
        activeDragGoalItem,
        activeDragGroupGoal,
        overlayWrapperStyle,
        isPendingDelete,
    };
};
